import type { CRMUser, EnhancedDeal } from '../types';
import { isDesignerRole } from './userRoles';

/** Active CRM users who do in-house design work (LCTF designers). */
export function listDesigners(crmUsers: CRMUser[]): CRMUser[] {
  return crmUsers.filter((u) => u.isActive && isDesignerRole(u.role));
}

export function isInternalDesignerUid(
  uid: string | undefined,
  crmUsers: CRMUser[],
): boolean {
  if (!uid) return false;
  const user = crmUsers.find((u) => u.uid === uid);
  return !!user && isDesignerRole(user.role);
}

/** @deprecated Use {@link isInternalDesignerUid}. */
export function isInternalCrmUserUid(
  uid: string | undefined,
  crmUsers: CRMUser[],
): boolean {
  return isInternalDesignerUid(uid, crmUsers);
}

/** External architecture firm / org (free text). Ignores leftover designer uids on `assignedArchitect`. */
export function getExternalArchitectName(
  deal: EnhancedDeal | null | undefined,
  crmUsers: CRMUser[],
): string {
  if (!deal) return '';
  if (isInternalDesignerUid(deal.assignedArchitect, crmUsers)) return '';
  return (deal.assignedArchitectName || '').trim();
}

/** In-house designer (Dan Kiser, etc.). Falls back to legacy `assignedArchitect` uid only when that user is a designer. */
export function getAssignedDesigner(
  deal: EnhancedDeal | null | undefined,
  crmUsers: CRMUser[],
): { uid: string; name: string } {
  if (!deal) return { uid: '', name: '' };

  if (deal.assignedDesigner) {
    const name =
      deal.assignedDesignerName ||
      crmUsers.find((u) => u.uid === deal.assignedDesigner)?.displayName ||
      '';
    return { uid: deal.assignedDesigner, name };
  }

  if (isInternalDesignerUid(deal.assignedArchitect, crmUsers)) {
    const uid = deal.assignedArchitect!;
    const name =
      deal.assignedArchitectName ||
      crmUsers.find((u) => u.uid === uid)?.displayName ||
      '';
    return { uid, name };
  }

  return {
    uid: '',
    name: (deal.assignedDesignerName || '').trim(),
  };
}
